import React, { useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Card, CardContent, Skeleton, Stack } from "@mui/material";
import { GridRowId, GridRowSelectionModel } from "@mui/x-data-grid";
import { useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { ExpandMore } from "../../../../../assets/icons";
import { formatNumber } from "../../../../../common/utils";
import TableData from "../../../../../components/common/table/TableData";
import TableSelectActions from "../../../../../components/common/table/TableData/TableSelectActions";
import { ConsumptionPageDTO, NodesPageItemDTO } from "../../../../../datasources/api-client/data-contracts";
import { makeSnackbarMessage, publishSnackbarMessage } from "../../../../../state/view";
import { useCurrentTheme } from "../../../../../theme/theme";
import {
	getNodeConsumptionsColumns,
	getNodeConsumptionsRows,
	GreenrouterColumn,
	GreenrouterRow,
	nodeConsumptionsCols,
} from "../data";
import CustomPagination from "./components/CustomPagination";
import GridTable from "./components/GridTable";

type Props = {
	reload: VoidFunction;
	page: number;
	pages: number;
	pageSize: number;
	itemsCount: number;
	items: NodesPageItemDTO[];
	pageSizes: number[];
	onPageChange: (newPage: number) => void;
	onPageSizeChange: (newPageSize: number) => void;
	onAccordionOpen: (id: string) => void;
	loadingConsumptions: boolean;
	consumptionsData?: ConsumptionPageDTO;
	reloadConsumptions: VoidFunction;
	handleLoadMore?: VoidFunction;
	hasMore?: boolean;
	columns: GreenrouterColumn[];
	sortModel?: {
		field?: keyof GreenrouterRow;
		direction?: "asc" | "desc";
	};
	onSortChange?: (field: keyof GreenrouterRow) => void;
	onColumnsChange?: (cols: GreenrouterColumn[]) => void;
};

/**
 * AccordionTable
 *
 * Displays a list of operational units inside expandable accordions.
 * Each accordion reveals a detailed data table (consumptions) for that node.
 * Supports:
 * - Row selection within tables
 * - Dynamic pagination and page size controls
 * - Lazy loading (load more)
 */
export default function AccordionTable({
	page,
	pages,
	pageSize,
	itemsCount,
	items,
	pageSizes,
	onPageChange,
	onPageSizeChange,
	onAccordionOpen,
	loadingConsumptions,
	consumptionsData,
	reloadConsumptions,
	handleLoadMore,
	hasMore,
	columns,
	sortModel,
	onSortChange,
	onColumnsChange,
}: Props) {
	const { t } = useTranslation("feature.units");
	const { theme } = useCurrentTheme();
	const publishMessage = useSetAtom(publishSnackbarMessage);

	// Track currently expanded accordion
	const [expanded, setExpanded] = useState<string | false>("");

	// Track selected rows and their IDs
	const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>({
		type: "include",
		ids: new Set<GridRowId>(),
	});

	// Handle selection of table rows, filter consumptionsData accordingly
	const handleRowSelection = (newSelection: GridRowSelectionModel) => {
		setSelectedRowIds(newSelection);
	};

	// Handle accordion expand/collapse and trigger data loading for that node
	const handleAccordionChange = (id: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
		setExpanded(isExpanded ? id : false);
		if (isExpanded) onAccordionOpen(id);
	};

	// Memoize table columns and rows (avoid recalculating unnecessarily)
	const tableColumns = consumptionsData
		? getNodeConsumptionsColumns(nodeConsumptionsCols, t, theme.palette.primary.contrastText)
		: [];
	const tableRows = consumptionsData ? getNodeConsumptionsRows(consumptionsData, t) : [];

	// IDs of selected rows to pass to actions
	const selectedRowsId =
		selectedRowIds.type === "include"
			? Array.from(selectedRowIds.ids)
			: consumptionsData?.items.filter((row) => !selectedRowIds.ids.has(row.id)).map((row) => row.id);

	const handleDeleteRows = () => {
		// Implement the logic to delete the selected rows here
		console.log("Deleting rows with IDs:", selectedRowsId);

		// Show success message
		// TODO: Message if the deletion is not successful after API integration
		publishMessage(makeSnackbarMessage(t("delete rows dialog/success message"), "success"));
	};

	return (
		<Card
			sx={{
				borderRadius: 2,
				boxShadow: 3,
				display: "flex",
				flexDirection: "column",
				height: "100%",
				width: "100%",
				overflowX: "hidden",
				minWidth: 0,
			}}
		>
			<CardContent
				sx={{
					display: "flex",
					flexDirection: "column",
					flexGrow: 1,
					p: 0,
					minWidth: "100%",
					overflowX: "scroll",
				}}
			>
				<Stack sx={{ flexGrow: 0, minWidth: "100%", width: "fit-content", overflowY: "hidden" }}>
					{/* Header Row  */}
					<GridTable
						isHeader
						columns={columns}
						sortModel={sortModel}
						onSortChange={onSortChange}
						onColumnsChange={onColumnsChange}
					/>

					{/* Accordion List */}
					{items.map((item) => (
						<Accordion
							key={item.id}
							expanded={expanded === item.id}
							onChange={handleAccordionChange(item.id)}
							sx={{
								mt: 0,
								width: "max-content",
								minWidth: "100%",
								paddingRight: "2px",
								background: expanded === item.id ? theme.palette.background.default : "transparent",
								"&:nth-of-type(even)": {
									backgroundColor: theme.palette.background.default,
								},
							}}
							slotProps={{ transition: { timeout: 0 } }}
						>
							{/* Accordion Header */}
							<AccordionSummary
								expandIcon={<ExpandMore />}
								sx={{
									pl: 0,
									height: 52,
									width: "max-content",
									minWidth: "100%",
									flexDirection: "row-reverse",
									"& .MuiAccordionSummary-expandIconWrapper": {
										transform: "rotate(0deg)",
										width: "24px",
										marginLeft: 1,
									},
									"&:hover:not(.Mui-expanded)": {
										backgroundColor: theme.palette.background.default,
									},
									"&.Mui-expanded .MuiAccordionSummary-expandIconWrapper": {
										transform: "rotate(-180deg)",
									},
								}}
							>
								<GridTable
									columns={columns}
									row={{
										id: item.id,
										code: item.code || "-",
										description: item.description || "-",
										typeDescription: { typeId: item.typeId, typeDescription: item.typeDescription },
										city: item.city || "-",
										country: item.country || "-",
										area: item.area ? formatNumber(item.area.value, "decimal", 2, 2) : "-",
										emissions: item.emissions?.value
											? formatNumber(item.emissions?.value, "decimal", 2, 2)
											: "-",
									}}
								/>
							</AccordionSummary>

							{/* Accordion Content (Consumption Table) */}
							<AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
								{!consumptionsData && loadingConsumptions ? (
									<Skeleton variant="rounded" height={400} width="100%" />
								) : (
									<>
										{/* Table actions when rows are selected */}
										{!loadingConsumptions && consumptionsData && (
											<TableSelectActions
												selectedRowsId={selectedRowsId}
												onDeleteRows={handleDeleteRows}
											/>
										)}

										{/* Table content */}
										{consumptionsData && (
											<TableData
												columns={tableColumns}
												rows={tableRows}
												isLoading={loadingConsumptions}
												onReload={reloadConsumptions}
												isSecondaryTable
												rowSelectionModel={selectedRowIds}
												onRowSelectionChange={handleRowSelection}
												onLoadMore={handleLoadMore}
												fixedHeight={400}
												hasMore={hasMore}
											/>
										)}
									</>
								)}
							</AccordionDetails>
						</Accordion>
					))}
					<CustomPagination
						page={page}
						pages={pages}
						pageSize={pageSize}
						itemsCount={itemsCount}
						pageSizes={pageSizes}
						onPageChange={onPageChange}
						onPageSizeChange={onPageSizeChange}
					/>
				</Stack>
			</CardContent>
		</Card>
	);
}
